// src/features/learner/UnitViewer.js 
import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import './UnitViewer.css';

// Custom Hooks
import { useUnitNavigation } from '../../hooks/useUnitNavigation';
import { useProgressTracking } from '../../hooks/useProgressTracking';
import { useUnitActivity } from '../../hooks/useUnitActivity';

// Components
import { ProgressBar } from './components/Navigation/ProgressBar';
import { UnitNavigation } from './components/Navigation/UnitNavigation';
import { ContentRenderer } from './components/ContentRenderers/ContentRenderer';
import { TutoringPanel } from './components/TutoringPanel';

// Utilities
import { renderAudioTokens, cleanupAudioPlayers } from '../../utils/audioHelpers';

// Styles
import '../../styles/StoryComponents.css';

// Statuses that count as teal (fully completed) for progress tracking
const TEAL_STATUSES = new Set(['correct', 'learnt', 'completed', true]);

// ---------------------------------------------------------------------------
// catalogId is now received from LessonView so package visits can be
// stamped with the catalog they belong to (required for leaderboard queries).
// ---------------------------------------------------------------------------
function UnitViewer({ lessonId, catalogId }) {
    const { currentUser } = useAuth();
    const containerRef = useRef(null);
    
    // Tutoring panel state
    const [showTutoringPanel, setShowTutoringPanel] = useState(false);

    // Custom hooks for major functionality
    const {
        loading,
        currentUnitIndex,
        currentInternalIndex,
        allContent,
        getCurrentContent,
        getInternalContent,
        goToNextUnit,
        goToPrevUnit,
        goToInternalIndex,
        touchHandlers
    } = useUnitNavigation(lessonId, currentUser);

    const {
        completedUnits,
        visitedInternalPages,
        visitedUnits,
        completedNavItems,
        userAnswers,
        showFeedback,
        markAsVisited,
        markAsCompleted,
        markPackageVisited,
        markNavItemCompleted,
        markInternalPageVisited,
        handleAnswerSelect,
        resetUnit,
        checkAnswer,
        getRandomizedOptions,
    } = useProgressTracking(currentUser);

    // Studied vs skipped: records time, pages and interactions per unit
    // (top-level item). See src/utils/unitActivity.js for the definition.
    const activityUnitId = allContent[currentUnitIndex]?.id || null;
    const { noteInteraction } = useUnitActivity({
        uid: currentUser?.uid,
        lessonId,
        catalogId,
        unitId: loading ? null : activityUnitId,
        internalIndex: currentInternalIndex
    });

    // Learner actions inside a unit count as interaction. (Automatic
    // completions done by this file itself, e.g. story pages, don't.)
    const onAnswerSelectTracked = (...args) => { noteInteraction(); return handleAnswerSelect(...args); };
    const onCheckAnswerTracked = (...args) => { noteInteraction(); return checkAnswer(...args); };
    const onMarkCompletedTracked = (...args) => { noteInteraction(); return markAsCompleted(...args); };
    const onResetUnitTracked = (...args) => { noteInteraction(); return resetUnit(...args); };

    // ---------------------------------------------------------------------------
    // Mark the current top-level nav item (package or standalone unit) as visited
    // whenever the learner moves to a new package.
    //
    // catalogId and lessonId are forwarded so the Firestore write records:
    //   visitedPackages.{packageId} = { catalogId, visitedAt }
    // This is what the Leaderboard component queries to rank learners.
    // ---------------------------------------------------------------------------
    useEffect(() => {
        const currentContent = getCurrentContent();
        if (!currentContent) return;
        markPackageVisited(currentContent.id, lessonId, catalogId);
    }, [currentUnitIndex, getCurrentContent, markPackageVisited, lessonId, catalogId]);

    // Track unit visits AND internal page visits - CONDITIONAL APPROACH
    useEffect(() => {
        const currentContent = getCurrentContent();
        const internalContent = getInternalContent();
        
        if (!currentContent) return;

        // STEP 1: Check if we have pagination
        const hasPagination = internalContent.length > 1;
        
        if (hasPagination) {
            // STEP 2: NUMBER TRACKING - Track specific pages/items
            const currentItem = internalContent[currentInternalIndex];
            
            if (currentItem) {
                // Track the specific page/item being viewed
                markAsVisited(currentItem.id);
                
                // Also track in the internal pages system
                markInternalPageVisited(currentInternalIndex, currentContent.id);
                
                // AUTO-COMPLETE story pages when visited (but NOT flipcards)
                if (['story', 'discussion', 'Assignment', 'interactive-image'].includes(currentContent.type)) {
                    // Mark this page as completed immediately
                    if (currentItem.id && currentItem.id.includes('_page_')) {
                        const pageMatch = currentItem.id.match(/_page_(\d+)$/);
                        const pageIndex = pageMatch ? parseInt(pageMatch[1], 10) : 0;
                        markAsCompleted(currentContent.id, pageIndex);
                       
                        // Check if all pages are now completed
                        const totalPages = internalContent.length;
                        let completedPages = 0;
                        
                        for (let i = 0; i < totalPages; i++) {
                            const pageCompletionId = `${currentContent.id}_page_${i}`;
                            const isCompleted = completedUnits[pageCompletionId] || (i === pageIndex);
                            if (isCompleted) completedPages++;
                        }                            
                        // If all pages are completed, mark the whole unit as completed
                        // BUT NOT for flipcard packages - they need user feedback
                        if (completedPages === totalPages && currentContent.type !== 'flipcard') {
                            markAsCompleted(currentContent.id);
                        }
                    } else {
                        // For package items - exclude ALL question types that need user feedback
                        const questionTypes = ['flipcard', 'flip-card', 'lrc', 'LRC', 'listen-record-compare', 
                                               'Type-in', 'MCQ', 'Multiple-response', 'Matching', 'Order'];
                        
                        if (!questionTypes.includes(currentItem.type)) {
                            markAsCompleted(currentItem.id);
                        }
                    }
                } 
            }
        } else {
            // STEP 3: BUTTON TRACKING - Track whole unit
            markAsVisited(currentContent.id);
            
            const questionTypes = ['flipcard', 'flip-card', 'lrc', 'LRC', 'listen-record-compare', 
                                   'Type-in', 'MCQ', 'Multiple-response', 'Matching', 'Order'];
            
            if (['story', 'discussion', 'Assignment', 'interactive-image'].includes(currentContent.type) 
                && !questionTypes.includes(currentContent.type)) {
                markAsCompleted(currentContent.id);
            } 
        }
    }, [currentUnitIndex, currentInternalIndex, getCurrentContent, getInternalContent, markAsVisited, markInternalPageVisited, markAsCompleted, completedUnits]);

    // ---------------------------------------------------------------------------
    // Watch completedUnits — when all dots in the current nav item are teal,
    // call markNavItemCompleted so CatalogView can count lesson completion.
    //
    // A nav item is complete when:
    //   - Standalone unit: completedUnits[unit.id] is a teal status
    //   - Package: every internal item has a teal status in completedUnits
    // ---------------------------------------------------------------------------
    useEffect(() => {
        const currentContent = getCurrentContent();
        const internalContent = getInternalContent();
        if (!currentContent) return;

        const hasPagination = internalContent.length > 1;

        if (hasPagination) {
            // Package: all internal items must be teal
            const allTeal = internalContent.every(item => {
                const status = completedUnits[item.id];
                // For story pages stored as packageId_page_N
                const pageMatch = item.id?.match(/_page_(\d+)$/);
                if (pageMatch) {
                    const pageStatus = completedUnits[`${currentContent.id}_page_${pageMatch[1]}`];
                    return TEAL_STATUSES.has(pageStatus) || TEAL_STATUSES.has(status);
                }
                return TEAL_STATUSES.has(status);
            });

            if (allTeal) {
                markNavItemCompleted(currentContent.id, lessonId);
            }
        } else {
            // Standalone unit: just check its own status
            const status = completedUnits[currentContent.id];
            if (TEAL_STATUSES.has(status)) {
                markNavItemCompleted(currentContent.id, lessonId);
            }
        }
    }, [completedUnits, currentUnitIndex]);

    // Handle audio tokens rendering
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const timeoutId = setTimeout(() => {
            cleanupAudioPlayers(container);
            
            const speechBubbles = container.querySelectorAll('.speech-bubble, .no-audio-tokens');
            speechBubbles.forEach(bubble => {
                bubble.setAttribute('data-skip-audio-tokens', 'true');
            });
            
            renderAudioTokens(container, false);
        }, 200);

        return () => {
            clearTimeout(timeoutId);
            cleanupAudioPlayers(container);
        };
    }, [getCurrentContent, currentUnitIndex, currentInternalIndex]);

    // ---------------------------------------------------------------------------
    // trackUnitCompleted — called on Next button press for content types
    // that require explicit navigation to be considered "done"
    // ---------------------------------------------------------------------------
    const trackUnitCompleted = (unitId) => {
        const currentContent = getCurrentContent();
        const internalContent = getInternalContent();

        if (!currentContent) return;

        const hasPagination = internalContent.length > 1;

        if (hasPagination) {
            const currentItem = internalContent[currentInternalIndex];
            
            if (currentItem && currentItem.id && currentItem.id.includes('_page_')) {
                const pageMatch = currentItem.id.match(/_page_(\d+)$/);
                const pageIndex = pageMatch ? parseInt(pageMatch[1], 10) : 0;
                markAsCompleted(currentContent.id, pageIndex);    
                
                const totalPages = internalContent.length;
                let completedPages = 0;
                
                for (let i = 0; i < totalPages; i++) {
                    const pageCompletionId = `${currentContent.id}_page_${i}`;
                    const isCompleted = completedUnits[pageCompletionId] || (i === pageIndex);
                    if (isCompleted) completedPages++;
                }
                
                if (completedPages === totalPages) {
                    markAsCompleted(currentContent.id);
                }
                
            } else {
                markAsCompleted(currentItem.id);
            }
            
        } else {
            markAsCompleted(unitId);
        }
    };

    // ---------------------------------------------------------------------------
    // When the learner navigates away from a unit, clear wrong-answer state so
    // they get a fresh attempt on return. Correct answers are left untouched.
    //
    // prevUnitRef stores the actual unit object at the END of each render so
    // that when the effect fires after navigation, we reliably know what was
    // just left — getInternalContent() already reflects the NEW position by
    // then and cannot be used to find the previous unit.
    //
    // resetUnitsRef guards against the effect firing multiple times for the
    // same unit due to completedUnits state updates triggering re-runs.
    // ---------------------------------------------------------------------------
    const prevUnitIndexRef = useRef(null);
    const prevInternalIndexRef = useRef(null);
    const prevUnitRef = useRef(null);
    const resetUnitsRef = useRef(new Set());

    useEffect(() => {
        const prevUnitIndex = prevUnitIndexRef.current;
        const prevInternalIndex = prevInternalIndexRef.current;
        const leftUnit = prevUnitRef.current;

        // Update refs to current position
        prevUnitIndexRef.current = currentUnitIndex;
        prevInternalIndexRef.current = currentInternalIndex;

        // Capture current unit object for next navigation
        const currentInternalContent = getInternalContent();
        prevUnitRef.current = currentInternalContent.length > 1
            ? currentInternalContent[currentInternalIndex]
            : getCurrentContent();

        // Skip on first mount
        if (prevUnitIndex === null || !leftUnit) return;

        // Skip if nothing actually changed
        if (prevUnitIndex === currentUnitIndex && prevInternalIndex === currentInternalIndex) return;

        // Clear reset-guard for the unit we just left so it can be reset
        // again if the learner returns, answers wrong, and leaves again
        resetUnitsRef.current.delete(leftUnit.id);

        // Reset wrong-answer state — but never for flipcard/LRC which use
        // their own still_learning/dunno persistence.
        // resetUnitsRef prevents calling resetUnit more than once per
        // navigation event even if this effect re-fires due to state updates.
        const noResetTypes = ['flipcard', 'flip-card', 'lrc', 'LRC', 'listen-record-compare'];
        const status = completedUnits[leftUnit.id];
        const isWrong = status === 'wrong' || status === 'dunno' || status === 'still_learning';

        if (isWrong && !noResetTypes.includes(leftUnit.type) && !resetUnitsRef.current.has(leftUnit.id)) {
            resetUnitsRef.current.add(leftUnit.id);
            resetUnit(leftUnit.id);
        }
    }, [currentUnitIndex, currentInternalIndex, completedUnits, resetUnit]);

    // Get current package title if viewing a package OR multi-page unit
const getCurrentPackageTitle = () => {
    const currentContent = getCurrentContent();
    if (!currentContent) return null;
    return currentContent.title || null;
};
    
    // Check if current content requires tutoring
    const requiresTutoring = () => {
        const currentContent = getCurrentContent();
        if (!currentContent) return false;
        return currentContent.requires_tutoring === true;
    };

    // Next unit navigation with completion tracking
    const handleNextUnit = () => {
        const currentContent = getCurrentContent();
        const internalContent = getInternalContent();
        
        if (currentContent) {
            const hasPagination = internalContent.length > 1;
            const questionTypes = ['flipcard', 'flip-card', 'lrc', 'LRC', 'listen-record-compare', 
                                   'Type-in', 'MCQ', 'Multiple-response', 'Matching', 'Order'];
            
            if (hasPagination) {
                if (['story', 'discussion', 'Assignment', 'interactive-image'].includes(currentContent.type)
                    && !questionTypes.includes(currentContent.type)) {
                    trackUnitCompleted(currentContent.id);
                }
            } else {
                if (['story', 'discussion', 'Assignment', 'interactive-image'].includes(currentContent.type)
                    && !questionTypes.includes(currentContent.type)) {
                    trackUnitCompleted(currentContent.id);
                }
            }
        }
        
        goToNextUnit();
    };

    if (loading) {
        return <div className="unit-viewer-loading">Loading content...</div>;
    }

    const currentContent = getCurrentContent();
    const internalContent = getInternalContent();
   
    return (
        <div className="unit-viewer" {...touchHandlers}>

            {/* ── TOP: progress bar, always visible ── */}
            <div className="unit-viewer-top">
                <ProgressBar
                    currentIndex={currentUnitIndex}
                    totalUnits={allContent.length}
                    internalIndex={currentInternalIndex}
                    internalTotal={internalContent.length}
                    completedNavItems={completedNavItems}
                    allContent={allContent}
                />

                {/* Package/Unit title + tutoring icon */}
                {(getCurrentPackageTitle() || requiresTutoring()) && (
                    <div className={`package-title-display
                        ${!getCurrentPackageTitle() ? 'icon-only' : ''}`}>
                        {getCurrentPackageTitle() && (
                            <span className="package-title-text">
                                {getCurrentPackageTitle()}
                            </span>
                        )}
                        {requiresTutoring() && (
                            <button
                                className="tutoring-icon-button"
                                onClick={() => setShowTutoringPanel(true)}
                                title="Live discussion required"
                            >
                                💬
                            </button>
                        )}
                    </div>
                )}
            </div>

            {/* ── MIDDLE: scrollable content area ── */}
            <div className="unit-viewer-content" ref={containerRef}>
                <ContentRenderer
                    content={internalContent.length > 0
                        ? internalContent[currentInternalIndex]
                        : currentContent}
                    userAnswers={userAnswers}
                    showFeedback={showFeedback}
                    completedUnits={completedUnits}
                    currentInternalIndex={currentInternalIndex}
                    internalContent={internalContent}
                    onAnswerSelect={onAnswerSelectTracked}
                    onCheckAnswer={onCheckAnswerTracked}
                    onMarkCompleted={onMarkCompletedTracked}
                    onResetUnit={onResetUnitTracked}
                    getRandomizedOptions={getRandomizedOptions}
                />
            </div>

            {/* ── BOTTOM: navigation, always visible ── */}
            <UnitNavigation
                currentIndex={currentUnitIndex}
                internalIndex={currentInternalIndex}
                totalUnits={allContent.length}
                internalContent={internalContent}
                completedUnits={completedUnits}
                visitedPages={visitedInternalPages}
                visitedUnits={visitedUnits}
                currentContent={currentContent}
                onPrevUnit={goToPrevUnit}
                onNextUnit={handleNextUnit}
                onGoToInternal={goToInternalIndex}
                onMarkPageVisited={markInternalPageVisited}
            />

            {/* Tutoring panel — overlays everything */}
            <TutoringPanel
                isOpen={showTutoringPanel}
                onClose={() => setShowTutoringPanel(false)}
                status="not_done"
            />

        </div>
    );
}

export default UnitViewer;